import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Paper, Grid, Switch, 
  FormControlLabel, TextField, Button, Chip, IconButton,
  Divider, Alert, CircularProgress, Card, CardContent, 
  List, ListItem, ListItemText, Snackbar, Accordion, AccordionSummary,
  AccordionDetails, Checkbox, FormGroup, FormControl, InputLabel, MenuItem,
  Select, Tooltip, ListItemIcon
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon, ExpandMore as ExpandMoreIcon, Info as InfoIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { modulesAPI } from '../api/api';
import NopigSettings from '../components/NopigSettings';

const MODULE_INFO = {
  'system': {
    title: 'Системный модуль',
    description: 'Модуль содержит системные настройки, влияющие на работу всех модулей.'
  },
  'grep': {
    title: 'Модуль фильтрации слов',
    description: 'Модуль grep удаляет сообщения, содержащие слова из списка запрещённых слов.'
  },
  'spam': {
    title: 'Защита от спама',
    description: 'Модуль удаляет спам-сообщения и блокирует спам-аккаунты.'
  },
  'mute': {
    title: 'Временное отключение чата',
    description: 'Модуль позволяет удалять все сообщения в чате на определенный период времени.'
  },
  'watchdog': {
    title: 'Фильтр контента',
    description: 'Модуль watchdog удаляет сообщения или выкидывает автора сообщения при совпадении с заданными фильтрами.'
  },
  'uname': {
    title: 'Контроль имен пользователей',
    description: 'Модуль uname ограничивает действия пользователей, если в их имени или логине обнаружены запрещённые слова.'
  },
  'langblock': {
    title: 'Фильтрация языков',
    description: 'Модуль langblock удаляет сообщения, написанные на запрещённом языке или содержащие символы запрещённого алфавита.'
  },
  'nopig': {
    title: 'Фильтрация ругательств',
    description: 'Модуль nopig удаляет сообщения, содержащие ругательства из настраиваемых фильтров.'
  },
  'nohello': {
    title: 'Фильтрация приветствий',
    description: 'Модуль nohello удаляет сообщения, содержащие только приветствие и ничего более.'
  },
  'nopm': {
    title: 'Фильтрация просьб в личку',
    description: 'Модуль nopm удаляет сообщения, в которых пользователь просит ответить ему в личные сообщения.'
  },
  'nohelp': {
    title: 'Фильтрация просьб о помощи',
    description: 'Модуль nohelp удаляет сообщения, содержащие только просьбу о помощи без конкретного вопроса.'
  },
  'sandbox': {
    title: 'Карантин пользователей',
    description: 'Модуль sandbox удаляет сообщения новых пользователей в зависимости от настроек карантина.'
  },
  'freq': {
    title: 'Ограничение частоты сообщений',
    description: 'Модуль freq ограничивает количество сообщений, которые пользователь может отправить за определенный период времени.'
  },
  'nosticker': {
    title: 'Контроль стикеров',
    description: 'Модуль nosticker позволяет управлять разрешенными и запрещенными стикерпаками в чате.'
  },
  'welcome': {
    title: 'Приветственные сообщения',
    description: 'Модуль welcome позволяет настроить приветственное сообщение для новых участников чата.'
  }
};

// Компонент для настроек watchdog модуля
const WatchdogSettings = ({ settings, onSettingsChange }) => {
  // Доступные фильтры с описаниями
  const WATCHDOG_FILTERS = {
    // Ссылки
    'link': 'Сообщения, содержащие ссылки',
    'mention': 'Сообщения с упоминаниями пользователей (@user)',
    'split_mention': 'Сообщения с разделенными упоминаниями (@user с пробелами)',
    'email': 'Сообщения, содержащие email адреса',
    'text_mention': 'Сообщения с текстовыми упоминаниями пользователей',
    
    // Медиа
    'sticker': 'Стикеры',
    'gif': 'GIF-анимации',
    'voice': 'Голосовые сообщения',
    'attachment': 'Вложенные файлы',
    'audio': 'Аудиозаписи',
    'video': 'Видеозаписи',
    'photo': 'Фотографии',
    'media_group': 'Группы медиа-файлов',
    'video_message': 'Видеосообщения',
    'not_sticker': 'Все, кроме стикеров',
    'story': 'Истории',
    'media_notext': 'Медиа без подписи',
    
    // Текст
    'command': 'Команды боту',
    'char1': 'Сообщения с одним символом',
    'only_emoji': 'Сообщения только с эмодзи',
    'emoji1': 'Сообщения с хотя бы одним эмодзи',
    'emoji2': 'Сообщения с хотя бы двумя эмодзи',
    'emoji3': 'Сообщения с хотя бы тремя эмодзи',
    'emoji4': 'Сообщения с хотя бы четырьмя эмодзи',
    'premium_emoji': 'Премиум эмодзи',
    'bold': 'Текст жирным шрифтом',
    'italic': 'Текст курсивом',
    'char250': 'Текст более 250 символов',
    'char500': 'Текст более 500 символов',
    'char1000': 'Текст более 1000 символов',
    'hashtag': 'Сообщения с хэштегами',
    'not_hashtag': 'Сообщения без хэштегов',
    'text': 'Любой текст',
    'cryptohash': 'Криптовалютные адреса',
    'uppercase': 'Текст заглавными буквами',
    'number': 'Числа',
    'number9': 'Числа с 9+ цифрами',
    'number11': 'Числа с 11+ цифрами',
    'bank_card_number': 'Номера банковских карт',
    'mixed_abc': 'Слова с буквами из разных алфавитов',
    'not_enru': 'Символы, отличные от русского и английского алфавитов',
    
    // Пользователи
    'bot': 'Сообщения от ботов',
    'bot_inviter': 'Пользователь, пригласивший бота',
    'new_user': 'Новые пользователи',
    'nousername': 'Пользователи без имени пользователя',
    'sender_chat': 'Сообщения от имени канала',
    'user_premium': 'Сообщения от пользователей с Telegram Premium',
    'via_chat_folder': 'Пользователи, присоединившиеся через папку чатов',
    
    // Другие фильтры
    'msg': 'Любые сообщения',
    'forwarded': 'Пересланные сообщения',
    'external_reply': 'Ответы на сообщения из других чатов',
    'button': 'Сообщения с кнопками',
    'poll': 'Опросы',
    'contact': 'Контакты',
    'game': 'Игры',
    'inline': 'Сообщения через inline режим',
    'dice': 'Эмодзи-кости',
    'location': 'Локации',
    'msg_premium': 'Сообщения от премиум-пользователей',
    'offline': 'Сообщения, отправленные в режиме offline'
  };

  // Категории фильтров для группировки
  const FILTER_CATEGORIES = {
    'Ссылки и упоминания': ['link', 'mention', 'split_mention', 'email', 'text_mention'],
    'Медиа-контент': ['sticker', 'gif', 'voice', 'attachment', 'audio', 'video', 'photo', 'media_group', 'video_message', 'not_sticker', 'story', 'media_notext'],
    'Текст и форматирование': ['command', 'char1', 'only_emoji', 'emoji1', 'emoji2', 'emoji3', 'emoji4', 'premium_emoji', 'bold', 'italic', 'char250', 'char500', 'char1000', 'hashtag', 'not_hashtag', 'text', 'cryptohash', 'uppercase', 'number', 'number9', 'number11', 'bank_card_number', 'mixed_abc', 'not_enru'],
    'Пользователи': ['bot', 'bot_inviter', 'new_user', 'nousername', 'sender_chat', 'user_premium', 'via_chat_folder'],
    'Другие фильтры': ['msg', 'forwarded', 'external_reply', 'button', 'poll', 'contact', 'game', 'inline', 'dice', 'location', 'msg_premium', 'offline']
  };

  // Обработчики изменения списков фильтров
  const handleFilterChange = (filterName, isChecked, filterType) => {
    const currentList = [...(settings[filterType] || [])];
    
    if (isChecked) {
      // Добавляем фильтр, если его еще нет в списке
      if (!currentList.includes(filterName)) {
        onSettingsChange({
          ...settings,
          [filterType]: [...currentList, filterName]
        });
      }
    } else {
      // Удаляем фильтр из списка
      onSettingsChange({
        ...settings,
        [filterType]: currentList.filter(filter => filter !== filterName)
      });
    }
  };

  // Обработчик изменения скрытых уведомлений
  const handleHideNotificationChange = (filterName, isChecked) => {
    const currentList = [...(settings.hide_notification || [])];
    
    if (isChecked) {
      // Добавляем фильтр в список скрытых уведомлений
      if (!currentList.includes(filterName)) {
        onSettingsChange({
          ...settings,
          hide_notification: [...currentList, filterName]
        });
      }
    } else {
      // Удаляем фильтр из списка скрытых уведомлений
      onSettingsChange({
        ...settings,
        hide_notification: currentList.filter(filter => filter !== filterName)
      });
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Настройки фильтров Watchdog
      </Typography>
      
      <Alert severity="info" sx={{ mb: 2 }}>
        Добавьте фильтры для удаления сообщений или исключения пользователей. 
        Фильтр будет применяться, если сообщение соответствует хотя бы одному из выбранных фильтров.
      </Alert>
      
      {Object.entries(FILTER_CATEGORIES).map(([category, filtersList]) => (
        <Accordion key={category} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{category}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              {filtersList.map(filterName => (
                <ListItem key={filterName}>
                  <FormGroup row sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={(settings.filter || []).includes(filterName)}
                          onChange={(e) => handleFilterChange(filterName, e.target.checked, 'filter')}
                        />
                      }
                      label="Удалить"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={(settings.kick_filter || []).includes(filterName)}
                          onChange={(e) => handleFilterChange(filterName, e.target.checked, 'kick_filter')}
                        />
                      }
                      label="Исключить"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={(settings.hide_notification || []).includes(filterName)}
                          onChange={(e) => handleHideNotificationChange(filterName, e.target.checked)}
                        />
                      }
                      label="Скрыть уведомление"
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2">
                        {filterName}
                        <Tooltip title={WATCHDOG_FILTERS[filterName]}>
                          <IconButton size="small">
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Typography>
                    </Box>
                  </FormGroup>
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

// Компонент для настроек модуля uname
const UnameSettings = ({ settings, onSettingsChange }) => {
  // Состояние для нового слова и регулярного выражения
  const [newNameWord, setNewNameWord] = useState('');
  const [newNameRegex, setNewNameRegex] = useState('');
  const [newLoginWord, setNewLoginWord] = useState('');
  const [newLoginRegex, setNewLoginRegex] = useState('');
  
  // Поддерживаемые типы алфавитов
  const SUPPORTED_SCRIPTS = {
    'arabic': 'Арабский алфавит',
    'cjk': 'Китайский, японский, корейский (CJK)'
  };
  
  // Обработчик добавления слова в запрещенные имена
  const handleAddNameWord = () => {
    if (!newNameWord.trim()) return;
    
    const nameWordList = [...(settings.name_word || [])];
    if (!nameWordList.includes(newNameWord.trim())) {
      onSettingsChange({
        ...settings,
        name_word: [...nameWordList, newNameWord.trim()]
      });
      setNewNameWord('');
    }
  };
  
  // Обработчик добавления регулярного выражения для имён
  const handleAddNameRegex = () => {
    if (!newNameRegex.trim()) return;
    
    const nameRegexList = [...(settings.name_regex || [])];
    if (!nameRegexList.includes(newNameRegex.trim())) {
      onSettingsChange({
        ...settings,
        name_regex: [...nameRegexList, newNameRegex.trim()]
      });
      setNewNameRegex('');
    }
  };
  
  // Обработчик добавления слова в запрещенные логины
  const handleAddLoginWord = () => {
    if (!newLoginWord.trim()) return;
    
    const loginWordList = [...(settings.login_word || [])];
    if (!loginWordList.includes(newLoginWord.trim())) {
      onSettingsChange({
        ...settings,
        login_word: [...loginWordList, newLoginWord.trim()]
      });
      setNewLoginWord('');
    }
  };
  
  // Обработчик добавления регулярного выражения для логинов
  const handleAddLoginRegex = () => {
    if (!newLoginRegex.trim()) return;
    
    const loginRegexList = [...(settings.login_regex || [])];
    if (!loginRegexList.includes(newLoginRegex.trim())) {
      onSettingsChange({
        ...settings,
        login_regex: [...loginRegexList, newLoginRegex.trim()]
      });
      setNewLoginRegex('');
    }
  };
  
  // Обработчик удаления элемента из списка
  const handleRemoveItem = (listName, index) => {
    const updatedList = [...(settings[listName] || [])];
    updatedList.splice(index, 1);
    
    onSettingsChange({
      ...settings,
      [listName]: updatedList
    });
  };
  
  // Обработчик изменения запрещенных алфавитов
  const handleScriptChange = (scriptName, isChecked) => {
    const scriptList = [...(settings.name_script || [])];
    
    if (isChecked) {
      // Добавляем алфавит, если его еще нет в списке
      if (!scriptList.includes(scriptName)) {
        onSettingsChange({
          ...settings,
          name_script: [...scriptList, scriptName]
        });
      }
    } else {
      // Удаляем алфавит из списка
      onSettingsChange({
        ...settings,
        name_script: scriptList.filter(script => script !== scriptName)
      });
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Настройки модуля контроля имён пользователей
      </Typography>
      
      <Alert severity="info" sx={{ mb: 2 }}>
        Настройте правила проверки имён и логинов пользователей. Пользователи, нарушающие правила, не смогут отправлять сообщения в чат.
      </Alert>
      
      {/* Настройки для имени пользователя */}
      <Accordion defaultExpanded sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">Настройки проверки имён пользователей</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Запрещенные слова в имени */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Запрещенные слова в имени
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  label="Запрещенное слово"
                  value={newNameWord}
                  onChange={(e) => setNewNameWord(e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddNameWord}
                  sx={{ ml: 1 }}
                >
                  Добавить
                </Button>
              </Box>
              
              <List dense>
                {(settings.name_word || []).map((word, index) => (
                  <ListItem 
                    key={index}
                    secondaryAction={
                      <IconButton 
                        edge="end" 
                        size="small"
                        onClick={() => handleRemoveItem('name_word', index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={word} />
                  </ListItem>
                ))}
                {(settings.name_word || []).length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    Список пуст
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>
          
          {/* Регулярные выражения для имени */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Регулярные выражения для проверки имени
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  label="Регулярное выражение"
                  value={newNameRegex}
                  onChange={(e) => setNewNameRegex(e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddNameRegex}
                  sx={{ ml: 1 }}
                >
                  Добавить
                </Button>
              </Box>
              
              <List dense>
                {(settings.name_regex || []).map((regex, index) => (
                  <ListItem 
                    key={index}
                    secondaryAction={
                      <IconButton 
                        edge="end" 
                        size="small"
                        onClick={() => handleRemoveItem('name_regex', index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={regex} />
                  </ListItem>
                ))}
                {(settings.name_regex || []).length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    Список пуст
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>
          
          {/* Запрещенные алфавиты */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Запрещенные алфавиты в имени
              </Typography>
              <FormGroup>
                {Object.entries(SUPPORTED_SCRIPTS).map(([scriptKey, scriptLabel]) => (
                  <FormControlLabel
                    key={scriptKey}
                    control={
                      <Checkbox 
                        checked={(settings.name_script || []).includes(scriptKey)}
                        onChange={(e) => handleScriptChange(scriptKey, e.target.checked)}
                      />
                    }
                    label={scriptLabel}
                  />
                ))}
              </FormGroup>
            </CardContent>
          </Card>
        </AccordionDetails>
      </Accordion>
      
      {/* Настройки для логина пользователя */}
      <Accordion defaultExpanded sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">Настройки проверки логинов пользователей</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Запрещенные слова в логине */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Запрещенные слова в логине
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  label="Запрещенное слово"
                  value={newLoginWord}
                  onChange={(e) => setNewLoginWord(e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddLoginWord}
                  sx={{ ml: 1 }}
                >
                  Добавить
                </Button>
              </Box>
              
              <List dense>
                {(settings.login_word || []).map((word, index) => (
                  <ListItem 
                    key={index}
                    secondaryAction={
                      <IconButton 
                        edge="end" 
                        size="small"
                        onClick={() => handleRemoveItem('login_word', index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={word} />
                  </ListItem>
                ))}
                {(settings.login_word || []).length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    Список пуст
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>
          
          {/* Регулярные выражения для логина */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Регулярные выражения для проверки логина
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  label="Регулярное выражение"
                  value={newLoginRegex}
                  onChange={(e) => setNewLoginRegex(e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddLoginRegex}
                  sx={{ ml: 1 }}
                >
                  Добавить
                </Button>
              </Box>
              
              <List dense>
                {(settings.login_regex || []).map((regex, index) => (
                  <ListItem 
                    key={index}
                    secondaryAction={
                      <IconButton 
                        edge="end" 
                        size="small"
                        onClick={() => handleRemoveItem('login_regex', index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={regex} />
                  </ListItem>
                ))}
                {(settings.login_regex || []).length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    Список пуст
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const ModuleSettingsPage = () => {
  const { chatId, moduleName } = useParams();
  const navigate = useNavigate();
  
  const [settings, setSettings] = useState({});
  const [originalSettings, setOriginalSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [newListItem, setNewListItem] = useState('');
  const [editingList, setEditingList] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Загрузка настроек модуля
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        console.log(`ModuleSettingsPage: Загрузка настроек модуля ${moduleName} для чата ${chatId}`);
        setLoading(true);
        setError(null);
        
        const response = await modulesAPI.getModuleSettings(chatId, moduleName);
        console.log(`ModuleSettingsPage: Получен ответ:`, response);
        
        if (response.data && response.data.settings) {
          console.log(`ModuleSettingsPage: Устанавливаем настройки:`, response.data.settings);
          setSettings(response.data.settings);
          setOriginalSettings(JSON.parse(JSON.stringify(response.data.settings))); // Сохраняем копию исходных настроек
          setHasUnsavedChanges(false);
        } else {
          console.error(`ModuleSettingsPage: Настройки отсутствуют в ответе API, получено:`, response.data);
          // Устанавливаем пустой объект настроек, чтобы избежать ошибок
          setSettings({
            active: false,
            notification: true
          });
          setOriginalSettings({
            active: false,
            notification: true
          });
          setError('Не удалось загрузить настройки модуля. Используются настройки по умолчанию.');
        }
        
        // Проверяем наличие ошибки в ответе API
        if (response.data && response.data.error) {
          console.error(`ModuleSettingsPage: Ошибка в ответе API:`, response.data.error);
          setError(`Ошибка: ${response.data.error}`);
        }
      } catch (err) {
        console.error('ModuleSettingsPage: Ошибка загрузки настроек модуля:', err);
        setError('Не удалось загрузить настройки модуля');
        // Устанавливаем пустой объект для избежания ошибок
        setSettings({
          active: false,
          notification: true
        });
        setOriginalSettings({
          active: false,
          notification: true
        });
      } finally {
        setLoading(false);
      }
    };

    if (chatId && moduleName) {
      fetchSettings();
    }
  }, [chatId, moduleName]);

  // Обработчик изменения скалярной настройки
  const handleScalarChange = (settingName, value) => {
    setSettings((prev) => ({
      ...prev,
      [settingName]: value
    }));
    
    setHasUnsavedChanges(true);
  };
  
  // Функция для сохранения только статуса активности модуля
  const saveSettingsWithStatus = async (activeStatus) => {
    setSaving(true);
    setError(null);
    
    try {
      // Создаем новый объект с текущими настройками и обновленным статусом
      const updatedSettings = {
        ...settings,
        active: activeStatus
      };
      
      console.log(`Сохранение статуса активности модуля ${moduleName}: ${activeStatus}`);
      await modulesAPI.updateModuleSettings(chatId, moduleName, updatedSettings);
      
      // После сохранения обновляем состояние
      setSettings(updatedSettings);
      setOriginalSettings(JSON.parse(JSON.stringify(updatedSettings)));
      setSuccess(true);
      setHasUnsavedChanges(false);
      
      // Для надежности перезагружаем настройки с сервера после короткого таймаута
      setTimeout(async () => {
        try {
          const response = await modulesAPI.getModuleSettings(chatId, moduleName);
          if (response.data && response.data.settings) {
            setSettings(response.data.settings);
            setOriginalSettings(JSON.parse(JSON.stringify(response.data.settings)));
          }
        } catch (err) {
          console.error('Ошибка при обновлении настроек после сохранения:', err);
        }
      }, 1000);
    } catch (err) {
      console.error('Ошибка при сохранении статуса активности:', err);
      setError('Не удалось сохранить статус активности модуля');
      
      // Восстанавливаем предыдущее значение в UI
      setSettings(prev => ({
        ...prev,
        active: !activeStatus
      }));
    } finally {
      setSaving(false);
    }
  };

  // Обработчик добавления элемента в список
  const handleAddListItem = (listName) => {
    if (!newListItem.trim()) return;
    
    setSettings((prev) => ({
      ...prev,
      [listName]: [...(prev[listName] || []), newListItem.trim()]
    }));
    
    setNewListItem('');
    setHasUnsavedChanges(true);
  };

  // Обработчик удаления элемента из списка
  const handleRemoveListItem = (listName, index) => {
    setSettings((prev) => ({
      ...prev,
      [listName]: prev[listName].filter((_, i) => i !== index)
    }));
    
    setHasUnsavedChanges(true);
  };

  // Обработчик изменения всех настроек
  const handleSettingsChange = (newSettings) => {
    setSettings(newSettings);
    setHasUnsavedChanges(true);
  };

  // Обработчик сохранения настроек
  const handleSaveSettings = async () => {
    setSaving(true);
    setError(null);
    
    try {
      await modulesAPI.updateModuleSettings(chatId, moduleName, settings);
      setSuccess(true);
      setHasUnsavedChanges(false);
      
      // Перезагружаем настройки с сервера для обеспечения актуальности данных
      setTimeout(async () => {
        try {
          const response = await modulesAPI.getModuleSettings(chatId, moduleName);
          if (response.data && response.data.settings) {
            setSettings(response.data.settings);
            setOriginalSettings(JSON.parse(JSON.stringify(response.data.settings)));
          }
        } catch (err) {
          console.error('Ошибка при обновлении настроек после сохранения:', err);
        }
      }, 1000);
    } catch (err) {
      console.error('Error saving module settings:', err);
      setError('Не удалось сохранить настройки модуля');
    } finally {
      setSaving(false);
    }
  };

  // Определение типа настройки: скалярный или список
  const isListSetting = (settingName, value) => {
    return Array.isArray(value);
  };

  // Отображение скалярной настройки
  const renderScalarSetting = (settingName, value) => {
    if (typeof value === 'boolean') {
      return (
        <FormControlLabel
          control={
            <Switch
              checked={value}
              onChange={(e) => handleScalarChange(settingName, e.target.checked)}
              color="primary"
            />
          }
          label={settingName === 'active' ? 'Активен' : (settingName === 'notification' ? 'Уведомления' : settingName)}
        />
      );
    } else if (typeof value === 'string') {
      return (
        <TextField
          label={settingName}
          value={value}
          onChange={(e) => handleScalarChange(settingName, e.target.value)}
          fullWidth
          margin="normal"
          variant="outlined"
        />
      );
    } else if (typeof value === 'number') {
      return (
        <TextField
          label={settingName}
          value={value}
          onChange={(e) => handleScalarChange(settingName, parseInt(e.target.value, 10) || 0)}
          fullWidth
          margin="normal"
          variant="outlined"
          type="number"
        />
      );
    }
    
    return null;
  };

  // Отображение настройки-списка
  const renderListSetting = (settingName, values) => {
    // Пропускаем отображение списков для модуля watchdog, они будут отображаться в специальном компоненте
    if (moduleName === 'watchdog' && ['filter', 'kick_filter', 'hide_notification'].includes(settingName)) {
      return null;
    }
    
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{settingName}</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setEditingList(editingList === settingName ? '' : settingName)}
            >
              {editingList === settingName ? 'Закрыть' : 'Добавить'}
            </Button>
          </Box>
          
          {/* Форма добавления элемента */}
          {editingList === settingName && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TextField
                label="Новое значение"
                value={newListItem}
                onChange={(e) => setNewListItem(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleAddListItem(settingName)}
                sx={{ ml: 1 }}
              >
                Добавить
              </Button>
            </Box>
          )}
          
          {/* Список элементов */}
          {values.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              Список пуст
            </Typography>
          ) : (
            <List dense>
              {values.map((item, index) => (
                <ListItem 
                  key={index}
                  secondaryAction={
                    <IconButton 
                      edge="end" 
                      size="small"
                      onClick={() => handleRemoveListItem(settingName, index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const moduleInfo = MODULE_INFO[moduleName] || { 
    title: moduleName, 
    description: 'Настройки модуля' 
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Button variant="outlined" onClick={() => navigate(`/chats/${chatId}`)} sx={{ mb: 2 }}>
          Назад к чату
        </Button>
        
        <Typography variant="h4" component="h1" gutterBottom>
          {moduleInfo.title}
        </Typography>
        
        <Typography variant="body1" paragraph>
          {moduleInfo.description}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {hasUnsavedChanges && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Есть несохраненные изменения. Нажмите "Сохранить настройки" для применения изменений.
          </Alert>
        )}
        
        <Paper sx={{ p: 3, mt: 2 }}>
          <Grid container spacing={3}>
            {/* Скалярные настройки */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Общие настройки
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {Object.entries(settings || {}).map(([key, value]) => (
                !isListSetting(key, value) && (
                  <Box key={key} sx={{ mb: 2 }}>
                    {renderScalarSetting(key, value)}
                  </Box>
                )
              ))}
            </Grid>
            
            {/* Списки */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Списки настроек
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {Object.entries(settings || {}).map(([key, value]) => (
                isListSetting(key, value) && (
                  <Box key={key}>
                    {renderListSetting(key, value)}
                  </Box>
                )
              ))}
            </Grid>
          </Grid>
          
          {/* Специальный компонент для настроек watchdog */}
          {moduleName === 'watchdog' && (
            <Grid item xs={12}>
              <WatchdogSettings 
                settings={settings} 
                onSettingsChange={handleSettingsChange} 
              />
            </Grid>
          )}
          
          {/* Специальный компонент для настроек uname */}
          {moduleName === 'uname' && (
            <Grid item xs={12}>
              <UnameSettings 
                settings={settings} 
                onSettingsChange={handleSettingsChange} 
              />
            </Grid>
          )}
          
          {/* Специальный компонент для настроек nopig */}
          {moduleName === 'nopig' && (
            <Grid item xs={12}>
              <NopigSettings 
                settings={settings} 
                onSettingsChange={handleSettingsChange} 
              />
            </Grid>
          )}
          
          <Box sx={{ mt: 3, textAlign: 'right' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              disabled={saving || !hasUnsavedChanges}
            >
              {saving ? 'Сохранение...' : 'Сохранить настройки'}
            </Button>
          </Box>
        </Paper>
      </Box>
      
      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
        message="Настройки успешно сохранены"
      />
    </Container>
  );
};

export default ModuleSettingsPage; 